import PropTypes from 'prop-types'
import { HStack, Button, Text, Center, Icon } from '@chakra-ui/react'
import { FiChevronLeft, FiChevronRight, FiMoreHorizontal } from 'react-icons/fi'

interface Props {
  currentPage: number
  totalPage: number
  onChangePage: (page: number) => void
  id?: string
}

const paginationButtonStyle = {
  minWidth: {
    base: 8,
    sm: 10,
    // md: 14,
  },
  height: {
    base: 8,
    sm: 10,
    // md: 14,
  },
  p: 2,
  fontSize: {
    base: 'sm',
    sm: 'md',
    md: 'lg',
  },
  color: 'inherit',
  borderRadius: 'md',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'normal',
  strokeWidth: '1px',
  textAlign: 'center',
  transition: 'background 300ms',
}

const clickablePaginationButtonStyle = {
  ...paginationButtonStyle,
  '&:not(:disabled):hover': {
    color: 'primary.400',
    fontWeight: 'medium',
  },
}

function Pagination({
  id,
  currentPage,
  totalPage,
  onChangePage,
}: Props): React.ReactElement {
  if (currentPage > totalPage) {
    console.error(
      `currentPage:[${currentPage}] should not be greater than totalPage:[${totalPage}]`,
    )
  }

  const _totalPage = Math.max(totalPage, 1)
  const _currentPage = Math.max(Math.min(currentPage, _totalPage), 1)

  return (
    <HStack
      id={id}
      spacing={{ base: 1, sm: 2 }}
      sx={{
        fontSize: 'md',
        fontWeight: 'medium',
        alignItems: 'center',
      }}
    >
      <Button
        sx={clickablePaginationButtonStyle}
        variant="ghost"
        colorScheme="primary"
        disabled={_currentPage <= 1}
        onClick={() => onChangePage(_currentPage - 1)}
      >
        <Icon as={FiChevronLeft} boxSize="full" />
      </Button>

      {
        // first page
        _currentPage > 1 && (
          <Button
            key={1}
            sx={clickablePaginationButtonStyle}
            variant="ghost"
            colorScheme="primary"
            onClick={() => onChangePage(1)}
          >
            1
          </Button>
        )
      }

      {
        // ... icon
        _currentPage > 3 && _totalPage > 5 && (
          <Center mx="8px" sx={paginationButtonStyle}>
            <Icon as={FiMoreHorizontal} boxSize="full" />
          </Center>
        )
      }

      {
        // when current page is last page and _totalPage === 5,
        // show "_currentPage - 3" number
        _totalPage === _currentPage && _totalPage === 5 && (
          <Button
            key={_currentPage - 3}
            sx={clickablePaginationButtonStyle}
            variant="ghost"
            colorScheme="primary"
            onClick={() => onChangePage(_currentPage - 3)}
          >
            {_currentPage - 3}
          </Button>
        )
      }

      {
        // when current page is last two page and total page is 5,
        // when current page is last page and total page is more than 3,
        // show "_currentPage - 2" number
        ((_totalPage - _currentPage < 2 && _totalPage === 5) ||
          (_totalPage - _currentPage < 1 && _totalPage > 3)) && (
          <Button
            key={_currentPage - 2}
            sx={clickablePaginationButtonStyle}
            variant="ghost"
            colorScheme="primary"
            onClick={() => onChangePage(_currentPage - 2)}
          >
            {_currentPage - 2}
          </Button>
        )
      }

      {
        // when current page is more than 2,
        // show "_currentPage - 1" number
        _currentPage > 2 && (
          <Button
            key={_currentPage - 1}
            sx={clickablePaginationButtonStyle}
            variant="ghost"
            colorScheme="primary"
            onClick={() => onChangePage(_currentPage - 1)}
          >
            {_currentPage - 1}
          </Button>
        )
      }

      <Center
        sx={{
          ...paginationButtonStyle,
          bg: 'primary.400',
          color: 'white',
          fontWeight: 'medium',
          '&:hover': {},
        }}
      >
        <Text>{_currentPage}</Text>
      </Center>

      {
        // when current page is not last page or page before last page,
        // show "_currentPage + 1" number
        _totalPage - _currentPage > 1 && (
          <Button
            key={_currentPage + 1}
            sx={clickablePaginationButtonStyle}
            variant="ghost"
            colorScheme="primary"
            onClick={() => onChangePage(_currentPage + 1)}
          >
            {_currentPage + 1}
          </Button>
        )
      }

      {
        // when current page is first two page and total page is 5,
        // when current page is first page and total page is more than 3,
        // show "_currentPage - 2" number
        ((_currentPage < 3 && _totalPage === 5) ||
          (_currentPage < 2 && _totalPage > 3)) && (
          <Button
            key={_currentPage + 2}
            sx={clickablePaginationButtonStyle}
            variant="ghost"
            colorScheme="primary"
            onClick={() => onChangePage(_currentPage + 2)}
          >
            {_currentPage + 2}
          </Button>
        )
      }

      {
        // when current page is first page and _totalPage is 5, show more number
        _currentPage < 2 && _totalPage === 5 && (
          <Button
            key={_currentPage + 3}
            sx={clickablePaginationButtonStyle}
            variant="ghost"
            colorScheme="primary"
            onClick={() => onChangePage(_currentPage + 3)}
          >
            {_currentPage + 3}
          </Button>
        )
      }

      {
        // ... icon
        _totalPage > 5 && _currentPage <= _totalPage - 3 && (
          <Center mx="8px" sx={paginationButtonStyle}>
            <Icon as={FiMoreHorizontal} boxSize="full" />
          </Center>
        )
      }

      {
        // ... last page
        _currentPage < _totalPage && (
          <Button
            key={_totalPage}
            sx={clickablePaginationButtonStyle}
            variant="ghost"
            colorScheme="primary"
            onClick={() => onChangePage(_totalPage)}
          >
            {_totalPage}
          </Button>
        )
      }

      <Button
        sx={clickablePaginationButtonStyle}
        variant="ghost"
        colorScheme="primary"
        disabled={_currentPage >= _totalPage}
        onClick={() => onChangePage(_currentPage + 1)}
      >
        <Icon as={FiChevronRight} boxSize="full" />
      </Button>
    </HStack>
  )
}

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPage: PropTypes.number.isRequired,
}

Pagination.defaultProps = {}

export default Pagination
